// Per-site report-data freshness (S5-2). The client half of `GET /reports/freshness`:
// the liveness → colour/label map, the "data through" relative formatter, and a small badge
// the multi-site reports page hangs off each site. The server derives every value
// (`heartbeat.site_freshness`); the client only paints it, so the two cannot disagree.

// Keyed on the server's `liveness` string. `online` is the calm case (no chrome shouting at a
// healthy fleet); `stale`/`offline` escalate amber → rose; the not-yet-live states are neutral.
export const FRESHNESS_META = {
  online:  { label: 'Live',    dot: 'bg-signal-ok-line', text: 'text-signal-ok', bg: 'bg-signal-ok-tint', border: 'border-signal-ok-line' },
  stale:   { label: 'Lagging', dot: 'bg-signal-warn-line',   text: 'text-signal-warn',   bg: 'bg-signal-warn-tint',   border: 'border-signal-warn-line' },
  offline: { label: 'Offline', dot: 'bg-signal-stop-line',    text: 'text-signal-stop',    bg: 'bg-signal-stop-tint',    border: 'border-signal-stop-line' },
  never:   { label: 'No data', dot: 'bg-ink-faint',   text: 'text-ink-soft',   bg: 'bg-surface-2',   border: 'border-hairline' },
  pending: { label: 'Pending', dot: 'bg-ink-faint',   text: 'text-ink-soft',   bg: 'bg-surface-2',   border: 'border-hairline' },
  no_node: { label: 'No node', dot: 'bg-ink-faint',   text: 'text-ink-soft',   bg: 'bg-surface-2',   border: 'border-hairline' },
  // A back-office site has no node by design. "No node" reads like a fault; "Imported"
  // says what is true, and the age beside it is the last sales upload rather than a
  // heartbeat. Never an alert — a venue uploading last month's figures on the 3rd is
  // working exactly as sold.
  imported: { label: 'Imported', dot: 'bg-verlo', text: 'text-verlo', bg: 'bg-verlo-tint', border: 'border-verlo' },
  revoked: { label: 'Revoked', dot: 'bg-signal-stop-line',    text: 'text-signal-stop',    bg: 'bg-signal-stop-tint',    border: 'border-signal-stop-line' },
};

// "data through <last sync>" — the same relative clock VerloNodePage draws for the heartbeat.
export const formatDataThrough = (iso) => {
  if (!iso) return 'no data yet';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 0) return 'just now';
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

// A compact per-site pill: liveness dot + label, and (unless the site has no data) the
// "data through" age. Rendered on the multi-site cards and ranking rows.
export const FreshnessBadge = ({ marker, className = '' }) => {
  if (!marker) return null;
  const meta = FRESHNESS_META[marker.liveness] || FRESHNESS_META.no_node;
  // An imported site shows the age of its last upload, or says there isn't one — never
  // the generic "no data yet", which reads as a fault at a venue that simply hasn't
  // uploaded yet.
  const imported = marker.liveness === 'imported';
  const showThrough = marker.liveness !== 'no_node' && marker.liveness !== 'pending'
    && !(imported && !marker.last_seen_at);
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap text-[12.5px] font-semibold px-1.5 py-0.5 rounded border ${meta.bg} ${meta.text} ${meta.border} ${className}`}
      data-testid={`freshness-badge-${marker.site_id}`}
      title={marker.reasons?.length ? marker.reasons.join(' · ') : undefined}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
      {showThrough && (
        <span className="font-normal opacity-70">· {formatDataThrough(marker.last_seen_at)}</span>
      )}
      {imported && !marker.last_seen_at && (
        <span className="font-normal opacity-70">· nothing yet</span>
      )}
    </span>
  );
};
